/**
 * PATH: packages/ui/src/kanban/components/card-detail/LiveSuggestionStrip.tsx
 * FILE_ID: UI-KANBAN-CARDDETAIL-LIVESUGGEST-001
 * PURPOSE: Live tick/cross AI suggestions while the operator TYPES a card
 *          description — debounced draft → suggest-actions endpoint →
 *          chips with ✓ (apply) / ✗ (drop). Nothing persists server-side:
 *          ✓ applies through the existing checklist / card-PATCH APIs.
 * WHY: Kanban client scoping U23 — "live time create actions
 *      recommendations … tick or cross". AiActionsBar is on-demand
 *      (click "Structure with AI"); this is the ambient half that watches
 *      the draft. Both confirm-to-apply — the AI never mutates on its own.
 * FLOW:
 *   text (typing) → 2.5s idle debounce → api.suggestCardActions
 *        → chips → ✓ createChecklistItem / updateCard /
 *                    createCardEntries (sub_card)   |   ✗ drop
 * DEPENDENCIES:
 *   - ../../api KanbanApi.suggestCardActions (optional — strip hides
 *     when the host/backend hasn't wired it) + CardLiveSuggestion
 * RELATED:
 *   - AiActionsBar.tsx: on-demand sibling; shares the apply semantics
 *   - email_kanban/views/card_actions/suggest_actions.py: the endpoint
 */

import { useEffect, useRef, useState } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import {
  CalendarClock,
  Check,
  GitBranch,
  Loader2,
  PencilLine,
  Sparkles,
  X,
} from 'lucide-react'
import type { CardLiveSuggestion, KanbanApi } from '../../api'
import type { Card } from '../../types'

export interface LiveSuggestionStripProps {
  /** The live draft text (the description textarea's current value). */
  text: string
  card: Card
  api: KanbanApi
  qc: QueryClient
  /** Bubble the updated card up so the drawer repaints dates/status. */
  onChanged?: (card: Card) => void
  /** Mirror an applied due date into the drawer's date control. */
  onDueApplied?: (iso: string) => void
  /** Debounce idle window (ms) — overridable for tests. */
  debounceMs?: number
}

// Mirror the server's MIN_DRAFT_CHARS so we don't post drafts the
// endpoint will short-circuit anyway.
const MIN_CHARS = 80

// Card fields a card_change ✓ may set — mirrors the backend accept
// whitelist (meeting_decisions._CARD_CHANGE_ALLOWED).
const CHANGE_FIELDS = ['status', 'priority', 'blocked_reason'] as const

type Row = { key: string; s: CardLiveSuggestion }

/** Next-work date on a suggestion — action_date preferred over legacy due_date. */
function suggestionActionDate(s: CardLiveSuggestion): string | null {
  const raw = s.action_date ?? s.due_date
  return typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null
}

export function LiveSuggestionStrip({
  text,
  card,
  api,
  qc,
  onChanged,
  onDueApplied,
  debounceMs = 2_500,
}: LiveSuggestionStripProps) {
  const [rows, setRows] = useState<Row[]>([])
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const lastSentRef = useRef('')
  const unavailableRef = useRef(false)
  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
    }
  }, [])

  const supported = typeof api.suggestCardActions === 'function'

  useEffect(() => {
    if (!supported) return
    const trimmed = (text ?? '').trim()
    if (
      trimmed.length < MIN_CHARS ||
      trimmed === lastSentRef.current ||
      unavailableRef.current
    )
      return
    const timer = setTimeout(() => {
      lastSentRef.current = trimmed
      setLoading(true)
      void api
        .suggestCardActions!(card.id, trimmed)
        .then((res) => {
          if (!aliveRef.current) return
          if (!res.available) {
            // Reasoner offline — go quiet for the rest of this drawer
            // session rather than re-posting every pause.
            unavailableRef.current = true
            return
          }
          // Guard hits (too_short / cooldown) return an empty list — keep
          // whatever the operator is already looking at. sub_card rows
          // need the entries bulk endpoint to apply, so hide them on
          // hosts that predate createCardEntries.
          const usable = res.suggestions.filter(
            (s) =>
              s.kind !== 'sub_card' ||
              typeof api.createCardEntries === 'function',
          )
          if (usable.length > 0) {
            setRows(usable.map((s, i) => ({ key: `${i}:${s.title}`, s })))
          }
        })
        .finally(() => {
          if (aliveRef.current) setLoading(false)
        })
    }, debounceMs)
    return () => clearTimeout(timer)
  }, [text, card.id, api, supported, debounceMs])

  // After all hooks — feature-detect contract (older hosts pay nothing).
  if (!supported) return null
  if (rows.length === 0 && !loading) return null

  const drop = (key: string) =>
    setRows((prev) => prev.filter((r) => r.key !== key))

  const apply = async (row: Row) => {
    if (busyKey != null) return
    setBusyKey(row.key)
    const { s } = row
    const actionDate = suggestionActionDate(s)
    try {
      if (s.kind === 'sub_card') {
        // Mint ONE real entry sub-card under this card (same endpoint as
        // the drawer's Entries table, ARC-3) — it lands on the board and
        // in the Entries section like any dependency child.
        await api.createCardEntries!(card.id, {
          rows: [
            {
              action: s.title,
              what: s.detail || undefined,
              date: actionDate ?? undefined,
            },
          ],
        })
        // Same keys useCardEntries invalidates so the Entries table +
        // board repaint (boardId unknown here — prefix match covers it).
        void qc.invalidateQueries({
          queryKey: ['kanban', 'card', card.id, 'subtree'],
        })
        void qc.invalidateQueries({ queryKey: ['kanban', 'cards'] })
      } else if (s.kind === 'action') {
        const lists = await api.listChecklists(card.id)
        const target = lists[0] ?? (await api.createChecklist(card.id, 'Actions'))
        await api.createChecklistItem({
          checklist: target.id,
          title: s.title,
          due_date: actionDate ?? undefined,
        })
        void qc.invalidateQueries({
          queryKey: ['kanban', 'card', card.id, 'checklists'],
        })
        // Checklist create (and item add) append Activity — refresh trail.
        void qc.invalidateQueries({ queryKey: ['kanban', 'card', card.id] })
      } else {
        const patch: Record<string, unknown> = {}
        for (const f of CHANGE_FIELDS) {
          if (s.payload && s.payload[f] !== undefined) patch[f] = s.payload[f]
        }
        // BUG-2026-08-05-KANBAN-SUGGEST-ACTION-DATE-FIRST
        if (actionDate) patch.action_date = actionDate
        if (Object.keys(patch).length > 0) {
          const updated = await api.updateCard(card.id, patch as never)
          onChanged?.(updated)
          if (actionDate) onDueApplied?.(actionDate)
        }
      }
      drop(row.key)
    } catch {
      // The scoped fetch layer dispatches `quotech.kanban.error` (host
      // toast). Keep the chip so the operator can retry.
    } finally {
      setBusyKey(null)
    }
  }

  return (
    <div className="mt-1.5" data-card-live-suggestions="">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          ) : (
            <Sparkles className="h-3 w-3" aria-hidden />
          )}
          AI suggests
        </span>
        {rows.map((row) => {
          const busy = busyKey === row.key
          const title =
            row.s.title.length > 40 ? `${row.s.title.slice(0, 40)}…` : row.s.title
          return (
            <span
              key={row.key}
              className="group inline-flex items-center overflow-hidden rounded-full border border-emerald-200 bg-emerald-50 text-[11px] text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300"
              title={row.s.detail || row.s.title}
            >
              <button
                type="button"
                onClick={() => void apply(row)}
                disabled={busyKey != null}
                aria-label={`Accept: ${row.s.title}`}
                className="inline-flex items-center gap-1 py-0.5 pl-2 pr-1.5 font-medium hover:bg-emerald-100 disabled:opacity-60 dark:hover:bg-emerald-900/50"
              >
                {busy ? (
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                ) : row.s.kind === 'card_change' ? (
                  <PencilLine className="h-3 w-3" aria-hidden />
                ) : row.s.kind === 'sub_card' ? (
                  <GitBranch className="h-3 w-3" aria-hidden />
                ) : (
                  <CalendarClock className="h-3 w-3" aria-hidden />
                )}
                <span className="max-w-[16rem] truncate">
                  {title}
                  {suggestionActionDate(row.s) && (
                    <span className="opacity-70"> · {suggestionActionDate(row.s)}</span>
                  )}
                </span>
                {!busy && (
                  <Check
                    className="h-3 w-3 opacity-50 transition-opacity group-hover:opacity-90"
                    aria-hidden
                  />
                )}
              </button>
              <button
                type="button"
                onClick={() => drop(row.key)}
                disabled={busyKey != null}
                aria-label={`Dismiss: ${row.s.title}`}
                className="border-l border-emerald-200 px-1 py-0.5 text-emerald-400 hover:bg-emerald-100 hover:text-emerald-700 dark:border-emerald-900/60 dark:hover:bg-emerald-900/50"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          )
        })}
      </div>
    </div>
  )
}
